import { useCallback, useState } from 'react';
import { Form, Formik, getIn } from 'formik';

import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';

import { BooleanFilter } from '@e-socna/utils';
import { getFragmentData } from '@e-socna/data-layer';

import { ConfirmBeforeExitForm } from '../../components/ConfirmBeforeExitForm';
import { FORM_LAYOUT_STYLE, FormLayout } from '../../components/FormLayout';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';

import { useMemos } from '../MemoBeforeSubmit/hooks/useMemos.hook';
import { MemoBeforeSubmitForm } from '../MemoBeforeSubmit';
import { memoAwareSubmitHandler } from '../MemoBeforeSubmit/utils/memo-aware-submit-handler.util';
import { AlertMessages } from '../../components/AlertMessages';

import { WorkflowActionRadio } from './WorkflowActionRadio';
import { ActionDialog } from './ActionDialog';
import { FolderBusinessEntityStateForm_ActionPermission_Fragment } from './fragments/action-permission.fragment';
import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import { LoadingPlaceholder } from './LoadingPlaceholder';
import type { WorkflowNode } from './types/workflow-node.type';
import type { ActionPermission } from './types/find-all-action-permissions-query.type';
import type { UseUpdateFolderBusinessEntityStateSubmit } from './types/use-submit.type';
import type { RenderActionDialog } from './types/action-dialog-render-props.type';
import type { AdditionalFieldsValues } from './types/additional-fields-render-props.type';
import { InfoAlert } from '../../components/InfoAlert';

interface Props {
  readonly actionPermissions: readonly ActionPermission[] | undefined;
  readonly areActionPermissionsLoading: boolean;
  readonly currentNode: WorkflowNode;
  readonly initialValues?: Readonly<AdditionalFieldsValues>;
  readonly onCancel: () => void;
  readonly renderActionDialog?: RenderActionDialog;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly title?: string;
  readonly useUpdateStateSubmit: UseUpdateFolderBusinessEntityStateSubmit;
}

export const FolderBusinessEntityStateForm = ({
  actionPermissions: pActionPermissions,
  areActionPermissionsLoading,
  currentNode,
  initialValues: pInitialValues,
  onCancel: handleCancel,
  renderActionDialog,
  setMustBlockAction,
  title,
  useUpdateStateSubmit,
}: Readonly<Props>) => {
  const { ui } = useUiContext();

  const { clearMemos, memos, setMemos } = useMemos();

  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);

  const openMemoDialog = useCallback(() => {
    setMemos((oldMemos) => ({
      ...oldMemos,
      isOpenMemoDialog: true,
    }));
  }, [setMemos]);

  const handleActionDialogValidate = useCallback(() => {
    setIsActionDialogOpen(false);
    openMemoDialog();
  }, [openMemoDialog]);

  const handleActionDialogCancel = useCallback(() => {
    setIsActionDialogOpen(false);
  }, []);

  const { formError, handleSubmit } = useUpdateStateSubmit({
    memos,
    onSuccess: handleCancel,
  });

  const { initialValues } = useInitialValuesFactory(pInitialValues);

  const submitLabel = 'Mettre à jour';

  const actionPermissions = getFragmentData(
    FolderBusinessEntityStateForm_ActionPermission_Fragment,
    pActionPermissions,
  );

  const actions = currentNode.actions
    .map((action) => {
      const actionPermission = actionPermissions?.find(
        (permission) => permission.actionId === action._id,
      );

      if (!actionPermission) {
        return null;
      }

      return (
        <WorkflowActionRadio
          actionDescription={action.description}
          actionId={action._id}
          actionLabel={action.label}
          actionPermission={actionPermission}
          key={action._id}
        />
      );
    })
    .filter(BooleanFilter);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {(formikBag) => {
        const {
          dirty,
          errors,
          handleBlur,
          handleChange,
          handleSubmit: formikSubmit,
          isSubmitting,
          isValid,
          submitForm,
          touched,
          values,
        } = formikBag;

        const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

        const actionDialog = renderActionDialog?.(formikBag) ?? null;

        const handleUpdateButtonClick = () => {
          if (actionDialog) {
            setIsActionDialogOpen(true);

            return;
          }

          openMemoDialog();
        };

        return (
          <Form
            onSubmit={memoAwareSubmitHandler({
              askMemo: handleUpdateButtonClick,
              mustAskMemo: dirty && isValid,
              submit: formikSubmit,
            })}
            // eslint-disable-next-line react/forbid-component-props
            style={FORM_LAYOUT_STYLE}
          >
            <ConfirmBeforeExitForm setMustBlockAction={setMustBlockAction} />
            <MemoBeforeSubmitForm
              clearMemos={clearMemos}
              isLoading={isLoading}
              memos={memos}
              onConfirm={submitForm}
              setMemos={setMemos}
              submitLabel={submitLabel}
            />
            {!!actionDialog && (
              <ActionDialog
                content={actionDialog.content}
                isOpen={isActionDialogOpen}
                isValid={actionDialog.isValid}
                onCancel={handleActionDialogCancel}
                onValidate={handleActionDialogValidate}
                title={actionDialog.title}
              />
            )}
            <FormLayout
              actions={
                <Stack direction="row" spacing={2}>
                  <Button
                    disabled={!(isValid && dirty)}
                    loading={isLoading}
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    {submitLabel}
                  </Button>
                  <Button
                    onClick={handleCancel}
                    size="large"
                    variant="outlined"
                  >
                    Annuler
                  </Button>
                </Stack>
              }
              error={
                !!formError && (
                  <AlertMessages messages={formError} severity="error" />
                )
              }
              title={title}
            >
              <Grid container spacing={2}>
                <Grid
                  size={12}
                  sx={{
                    display: 'flex',
                    flexGrow: 1,
                    pt: 1,
                    pb: 1,
                  }}
                >
                  <FormControl
                    error={!!(errors.actionId && touched.actionId)}
                    required
                    sx={{ display: 'flex', flexGrow: 1 }}
                    variant="standard"
                  >
                    <FormLabel id="folder-business-entity-state-radios">
                      Liste des actions
                    </FormLabel>
                    {areActionPermissionsLoading ? (
                      <>
                        <LoadingPlaceholder />
                        <LoadingPlaceholder />
                        <LoadingPlaceholder />
                      </>
                    ) : !actions.length ? (
                      <InfoAlert sx={{ mt: 2 }}>
                        Aucune action n&apos;est disponible pour ce statut.
                      </InfoAlert>
                    ) : (
                      <RadioGroup
                        name="actionId"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.actionId}
                      >
                        {actions}
                      </RadioGroup>
                    )}
                  </FormControl>
                  {!!(errors.actionId && touched.actionId) && (
                    <FormHelperText>{getIn(errors, 'actionId')}</FormHelperText>
                  )}
                </Grid>
              </Grid>
            </FormLayout>
          </Form>
        );
      }}
    </Formik>
  );
};
